import {
  Link,
  Navigate,
  createFileRoute,
  notFound,
} from "@tanstack/react-router";
import { z } from "zod/v4";
import { CheckIcon, XIcon } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const outcomeOptions = z.enum(["success", "failure"]);

const outcomeProps = {
  success: {
    Icon: CheckIcon,
    heading: "You’re All Set!",
    subheading: "Your payment has been processed successfully.",
    color: "var(--color-green-500)",
    metaTitle: "Payment Successful",
  },
  failure: {
    Icon: XIcon,
    heading: "Transaction Unsuccessful",
    subheading: "We couldn't process your payment. Please try again.",
    color: "var(--color-red-500)",
    metaTitle: "Payment Failed",
  },
} as const satisfies Record<
  z.infer<typeof outcomeOptions>,
  {
    Icon: ({ className }: { className: string }) => ReactNode;
    heading: string;
    subheading: string;
    color: string;
    metaTitle: string;
  }
>;

export const Route = createFileRoute("/_app/payment-confirmation/$outcome")({
  component: RouteComponent,
  loader({ params: { outcome } }) {
    const validatedOutcome = outcomeOptions.safeParse(outcome);
    if (!validatedOutcome.success) throw notFound();
    return { outcome: validatedOutcome.data };
  },
  notFoundComponent: () => <Navigate to="/" />,

  head: ({ params: { outcome } }) => ({
    meta: [
      {
        title:
          outcomeProps[outcome as z.infer<typeof outcomeOptions>].metaTitle +
          " | Polygraf AI Dashboard",
      },
    ],
  }),
});

function RouteComponent() {
  const outcome = Route.useLoaderData({ select: (s) => s.outcome });
  const { Icon, heading, subheading, color } = outcomeProps[outcome];
  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center p-4"
      style={{ "--icon-color": color } as CSSProperties}
    >
      <div className="mx-auto max-w-md text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
          <span className="aspect-square rounded-full bg-(--icon-color)/10 p-2">
            <Icon className="size-10 text-(--icon-color)" />
          </span>
        </div>
        <h1 className="mb-3 text-3xl font-semibold tracking-tight text-gray-900 md:text-4xl">
          {heading}
        </h1>
        <p className="mb-8 text-gray-600">{subheading}</p>
        {outcome == "failure" ? (
          <div className="flex flex-col justify-center space-y-3 sm:flex-row sm:space-y-0 sm:space-x-3">
            <Link
              to="/pricing"
              className={cn(
                buttonVariants({ variant: "outline" }),
                "border border-gray-500 bg-transparent text-gray-500",
              )}
            >
              Back to Pricing
            </Link>
            <Link
              to="/"
              className={cn(
                buttonVariants({ variant: "outline" }),
                "border-gray-200 text-gray-700",
              )}
            >
              Go back to Dashboard
            </Link>
          </div>
        ) : (
          <Link
            to={"/"}
            className={cn(
              buttonVariants({ variant: "outline" }),
              "border-gray-200 text-gray-700",
            )}
          >
            Start now!
          </Link>
        )}{" "}
      </div>
    </div>
  );
}
